import type { Milestone } from "@schema/catalog.ts";
import { useId } from "react";
import { formatDays, formatDeadline } from "@/lib/countdown/format.ts";
import { useSettingsStore } from "@/state/settings.ts";
import styles from "./countdown.module.css";

/**
 * The switch that splits the road in two.
 *
 * Its hint does the persuading. Off, it states the thing people get wrong —
 * the Encore is not some footnote in December, it's weeks away — and names
 * the gap in days. On, it keeps naming it, because the gap is the reason the
 * two legs read so differently.
 *
 * Hidden entirely when the catalog has no splitting milestone: a toggle that
 * can't do anything shouldn't be on screen.
 */
export function EncoreToggle({
  milestone,
  leadDays,
}: {
  milestone: Milestone | null;
  /** How many days sooner the milestone lands than the target. */
  leadDays: number;
}) {
  const encoreMode = useSettingsStore((state) => state.encoreMode);
  const setEncoreMode = useSettingsStore((state) => state.setEncoreMode);
  const id = useId();

  if (!milestone) return null;

  return (
    <div className={styles.encore}>
      <label className={styles.encoreSwitch} htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={encoreMode}
          onChange={(event) => setEncoreMode(event.target.checked)}
        />
        <span className={styles.encoreLabel}>Encore mode</span>
      </label>
      <p className={styles.encoreHint}>
        {encoreMode ? (
          <>
            Two deadlines. Everything through Endgame is due{" "}
            <strong>{formatDeadline(milestone.date)}</strong>, {formatDays(leadDays)} before
            Doomsday.
          </>
        ) : (
          <>
            <strong>{milestone.title}</strong> is in theaters {formatDeadline(milestone.date)},{" "}
            {formatDays(leadDays)} before Doomsday. Split the road to see what's really due then.
          </>
        )}
      </p>
    </div>
  );
}
